import { ref } from "vue";
import api from "@/api";
import router from "@/router";
import { getStoredAccessData, hasValidSession } from "@/publicjs/authSession";
import { openAccessPermissionStream } from "@/utils/accessPermissionStream";

const MODULES_STORAGE_KEY = "modules";
export const MODULES_UPDATED_EVENT = "erp-modules-updated";

export const modulesState = ref([]);
export const modulesLoadingState = ref(true);

let stopAccessModulesStream = null;
let activeStreamUserId = null;

const normalizeModules = (modules) => (Array.isArray(modules) ? modules : []);

export const shouldUseRealtimeAccessModules = () => {
  try {
    const apiUrl = new URL(process.env.VUE_APP_API_URL);
    return !["localhost", "127.0.0.1"].includes(apiUrl.hostname);
  } catch (error) {
    return process.env.NODE_ENV === "production";
  }
};

const parseStoredModules = () => {
  const storedModules = localStorage.getItem(MODULES_STORAGE_KEY);

  if (!storedModules) {
    return [];
  }

  try {
    return normalizeModules(JSON.parse(storedModules));
  } catch (error) {
    return [];
  }
};

const emitModulesUpdated = (modules) => {
  window.dispatchEvent(
    new CustomEvent(MODULES_UPDATED_EVENT, {
      detail: {
        modules,
      },
    }),
  );
};

export const syncModulesState = (modules) => {
  const normalizedModules = normalizeModules(modules);

  modulesState.value = normalizedModules;
  localStorage.setItem(MODULES_STORAGE_KEY, JSON.stringify(normalizedModules));
  emitModulesUpdated(normalizedModules);

  return normalizedModules;
};

export const hydrateModulesState = () => {
  const cachedModules = parseStoredModules();
  modulesState.value = cachedModules;
  return cachedModules;
};

export const hasRouteModuleAccess = (path, modules = modulesState.value) => {
  const normalizedPath = String(path || "")
    .split("?")[0]
    .trim();

  if (
    normalizedPath === "" ||
    normalizedPath === "/" ||
    normalizedPath === "/login" ||
    normalizedPath.startsWith("/seguimiento/") ||
    normalizedPath.startsWith("/taller/seguimiento/")
  ) {
    return true;
  }

  return normalizeModules(modules).some((module) =>
    Array.isArray(module?.submodulos)
      ? module.submodulos.some((submodule) => submodule?.url === normalizedPath)
      : false,
  );
};

export const ensureCurrentRouteModuleAccess = (
  modules = modulesState.value,
) => {
  const currentRoutePath =
    router.currentRoute.value?.path || window.location.pathname || "/";

  if (
    !hasValidSession() ||
    !currentRoutePath ||
    currentRoutePath === "/login"
  ) {
    return;
  }

  if (!hasRouteModuleAccess(currentRoutePath, modules)) {
    router.replace("/").catch(() => null);
  }
};

export const refreshMyModules = async () => {
  const accessData = getStoredAccessData();
  const currentUserId = Number(accessData?.user?.key || 0);

  if (!currentUserId) {
    modulesLoadingState.value = false;
    return syncModulesState([]);
  }

  modulesLoadingState.value = true;

  try {
    const response = await api.get(`mis_modulos/${currentUserId}`);
    const modules = syncModulesState(response?.data || []);

    ensureCurrentRouteModuleAccess(modules);

    return modules;
  } catch (error) {
    const cachedModules = hydrateModulesState();
    return cachedModules;
  } finally {
    modulesLoadingState.value = false;
  }
};

export const startAccessModulesStream = () => {
  const accessData = getStoredAccessData();
  const currentUserId = Number(accessData?.user?.key || 0);

  if (
    !currentUserId ||
    !hasValidSession() ||
    !shouldUseRealtimeAccessModules()
  ) {
    return null;
  }

  if (stopAccessModulesStream && activeStreamUserId === currentUserId) {
    return stopAccessModulesStream;
  }

  if (stopAccessModulesStream) {
    stopAccessModulesStream();
  }

  activeStreamUserId = currentUserId;
  stopAccessModulesStream = openAccessPermissionStream({
    onSnapshot: (payload) => {
      const affectedUserIds = Array.isArray(payload?.meta?.affected_user_ids)
        ? payload.meta.affected_user_ids.map((value) => Number(value))
        : [];

      if (
        affectedUserIds.length > 0 &&
        !affectedUserIds.includes(currentUserId)
      ) {
        return;
      }

      const modules = syncModulesState(payload?.snapshot || []);
      modulesLoadingState.value = false;
      ensureCurrentRouteModuleAccess(modules);
    },
    onError: () => {
      modulesLoadingState.value = false;
    },
  });

  return stopAccessModulesStream;
};
